import { Component, OnInit, OnDestroy, ViewEncapsulation, ViewChild, TemplateRef, signal } from '@angular/core';
import { ClientIntegrationService } from '../../services/client-integration.service';
import { forkJoin } from 'rxjs';
import { User } from '../../models/user';
import { Integration } from 'src/app/models/integration';
import { ClientConfigurationSecretService } from 'src/app/services/client-configuration-secret.service';
import { ClientConfigurationSecret } from 'src/app/models/client-config-secret';
import { ConfigurationPath } from 'src/app/models/configuration-path';
import { ClientInformation } from 'src/app/models/client-information';
import { IntegrationIdentifier } from 'src/app/models/integration-identifier';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs/internal/Subject';
import { ToastService } from 'src/app/services/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { IdentifierService } from 'src/app/services/identifier.service';
import { ColumnSettings } from 'src/app/models/column-settings';
import { environment } from 'src/environments/environment';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { createEditButton, GridSort } from 'src/app/features/grid/classes/grid-config.types';

@Component({
  selector: 'app-client-configuration-secrets',
  templateUrl: './client-configuration-secrets.component.html',
  styleUrls: ['./client-configuration-secrets.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [ClientIntegrationService],
  standalone: false,
})
export class ClientConfigurationSecretsComponent implements OnInit, OnDestroy {
  //Class variables
  @ViewChild('confirmDialog', { static: true }) confirmDialog: TemplateRef<any>;
  @ViewChild('migrateDialog', { static: true }) migrateDialog: TemplateRef<any>;
  componentDestroyed$ = new Subject<boolean>();

  secretsGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  //Selected values from generic grids
  selectedClient: ClientInformation = null;
  selectedIntegration: Integration = null;
  configurationPath: ConfigurationPath = null;
  identifiers: IntegrationIdentifier[] = [];

  // all groups for generic grids
  allClients: ClientInformation[] = [];
  integrations: Integration[] = [];
  allGroupConfigPath: ConfigurationPath[] = [];
  matchingSecrets: ClientConfigurationSecret[] = [];
  gettingClients = false;

  //Secret grid variables
  allSecrets: ClientConfigurationSecret[] = [];

  //Status variables
  editingSecret = false;
  editingPath = false;
  addingSecret = false;

  // Loading variables
  gettingConfigPaths = false;
  editAddSecretLoading = false;

  //Object variables
  clientConfigurationSecret: ClientConfigurationSecret;
  secretsToUpdate: ClientConfigurationSecret[];
  secretValue: string;

  showMigration = false;
  migratingSecret = false;

  // column settings for generic grids
  clientColumns: ColumnSettings[] = [
    { field: 'clientDisplayName', title: 'Client Name', type: 'text', width: 100 },
    { field: 'losTalkerClientId', title: 'Client ID', type: 'text', width: 50 },
    { field: 'dataSourceTypeAgg', title: 'Data Source Type Id', type: 'text', width: 100 },
  ];
  matchingSecretsColumns: ColumnSettings[] = [
    { field: 'integrationName', title: 'Integration Name', type: 'text', width: 165, filterable: false },
    { field: 'attributeName', title: 'AttributeName', type: 'text', width: 100, filterable: false },
    { field: 'elementPath', title: 'ElementPath', type: 'text', width: 110, filterable: false },
    { field: 'displayName', title: 'DisplayName', type: 'text', width: 175, filterable: false },
    { field: 'updatedDate', title: 'UpdatedDate', type: 'text', width: 155, filterable: false },
  ];
  integrationColumns: ColumnSettings[] = [
    { field: 'name', title: 'Name', type: 'text', width: 80, filterable: false },
    { field: 'dataSourceTypeId', title: 'Data Source Type Id', type: 'text', width: 100, filterable: false },
    { field: 'integrationId', title: 'ID', type: 'text', width: 200, filterable: false },
  ];
  configPathColumns: ColumnSettings[] = [
    { field: 'displayName', title: 'DisplayName', type: 'text', width: 100 },
    { field: 'elementPath', title: 'ElementPath', type: 'text', width: 125 },
    { field: 'attributeName', title: 'AttributeName', type: 'text', width: 75 },
  ];
  // sort for client grid

  clientSortDescriptions: GridSort[] = [{ field: 'clientDisplayName', order: 1 }];


  constructor(
    private clientConfigurationSecretService: ClientConfigurationSecretService,
    private clientIntegrationService: ClientIntegrationService,
    private identifierService: IdentifierService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private user: User,
  ) {
    this.gettingClients = true;
    this.secretsGridSettings.set(this.setupSecretGrid());
    this.clientIntegrationService.getAllClients()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (r) => {
          this.allClients = r;
          this.gettingClients = false;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to get Clients.', 'Warning');
          this.gettingClients = false;
        },
      });
  }

  ngOnInit(): void {
    this.gettingConfigPaths = true;
    this.clientConfigurationSecretService.getConfigurationPaths()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (paths) => {
          this.allGroupConfigPath = paths;
          this.gettingConfigPaths = false;
        },
        error: (_error) => {
          this.toastService.toastCreate('Failed to get Configuration Paths.', 'Warning');
          this.gettingConfigPaths = false;
        },
      });
  }

  public ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  addSecret(): void {
    this.addingSecret = true;
    this.editingPath = true;
  }

  cancelAddingEditingSecret(): void {
    this.addingSecret = false;
    this.editingSecret = false;
    this.selectedIntegration = null;
    this.configurationPath = null;
    this.editingPath = false;
    this.secretValue = undefined;
    this.showMigration = false;
    // this.clientConfigurationSecret = null;
    // this.secretsToUpdate = [];
  }


  confirmAdd() {

    if (!this.configurationPath && !this.selectedIntegration && !this.secretValue) {
      this.toastService.toastCreate('All fields must be populated to add secret.', 'Danger');
      return;
    }

    this.clientConfigurationSecret = null;
    const secret: ClientConfigurationSecret = {
      clientConfigurationSecretId: undefined,
      displayName: this.configurationPath.displayName,
      azureSecretId: null,
      azureSecretVersion: null,
      azureName: null,
      deleted: false,
      updatedDate: new Date(),
      integrationId: this.selectedIntegration.integrationId,
      integrationName: this.selectedIntegration.name,
      serverId: this.selectedIntegration.serverId,
      clientId: this.selectedIntegration.clientId,
      secretValue: this.secretValue,
      configurationPathId: this.configurationPath.configurationPathId,
      elementPath: this.configurationPath.elementPath,
      attributeName: this.configurationPath.attributeName,
      destinationEndpointId: this.configurationPath.destinationEndpointId,
      dataSourceTypeId: this.configurationPath.dataSourceTypeId,
      aWSDisplayName: null,
      aWSResourceName: null,
      aWSVersionId: null,
    };

    this.clientConfigurationSecret = secret;

    this.secretsToUpdate = [this.clientConfigurationSecret];

    this.dialog.open(this.confirmDialog);
  }

  submitAddSecret(): void {
    this.editAddSecretLoading = true;
    this.clientConfigurationSecretService.addClientConfigSecret(this.clientConfigurationSecret)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: () => {

          this.clientConfigurationSecret = null;
          this.secretsToUpdate = [];

          this.toastService.toastCreate('Secret added.', 'Success');

          this.refreshClientSecrets();

          this.cancelAddingEditingSecret();

          this.dialog.closeAll();
          this.editAddSecretLoading = false;

        },
        error: (_error) => {
          this.dialog.closeAll();
          this.editAddSecretLoading = false;
        },
      });

  }

  editClient() {
    this.selectedClient = null;
    this.integrations = [];
    this.identifiers = [];
    this.allSecrets = [];
  }

  editSecret(clientConfigurationSecret: ClientConfigurationSecret) {
    this.matchingSecrets = [];

    this.clientConfigurationSecretService
      .getClientConfigSecret(clientConfigurationSecret.clientConfigurationSecretId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (response) => {
          this.clientConfigurationSecret = response;

          if (this.clientConfigurationSecret?.migrationNeeded == true) {
            this.showMigration = true;
          }

          //Find duplicate secrets
          let secretMatches: ClientConfigurationSecret[] = [];

          const m = this.allSecrets.filter(x =>
            x.clientConfigurationSecretId != this.clientConfigurationSecret.clientConfigurationSecretId
            && x.aWSResourceName == this.clientConfigurationSecret.aWSResourceName
            && x.elementPath == this.clientConfigurationSecret.elementPath
            && x.attributeName == this.clientConfigurationSecret.attributeName,
          );
          if (m != null && m.length > 0) {
            secretMatches = m;
          }

          console.log("secret matches")
          console.log(secretMatches)

          this.secretValue = undefined;
          this.configurationPath = null;
          this.selectedIntegration = this.integrations.find(x => x.integrationId == this.clientConfigurationSecret.integrationId);

          if (secretMatches.length == 0) {
            this.editingSecret = true;
          }
          else {
            if (this.showMigration || secretMatches.some(x => x.migrationNeeded == true)) {
              this.toastService.toastCreate('There may be ' + secretMatches.length + ' additional secrets to migrate', 'Info');
              this.editingSecret = true;
            }
            else {
              this.allSecrets.forEach(secret => {
                if (secretMatches.filter(matchedSecret => matchedSecret.clientConfigurationSecretId == secret.clientConfigurationSecretId).length > 0)
                {
                  this.matchingSecrets.push(secret);
                }
              });
              this.editingSecret = true;
            }
          }
        },
      });
  }

  submitEditSecret() {
    this.submitEditingSecrets([this.clientConfigurationSecret]);
  }

  submitEditMultipleSecrets() {
    this.submitEditingSecrets(this.secretsToUpdate);
  }

  submitEditingSecrets(secretsToUpdate: ClientConfigurationSecret[]) {
    this.editAddSecretLoading = true;

    if (this.secretValue?.trim() != '' && this.secretValue != null) {
      secretsToUpdate.forEach(secret => secret.secretValue = this.secretValue);
    }

    if (this.configurationPath != null) {
      secretsToUpdate.forEach(secret => {
        secret.configurationPathId = this.configurationPath.configurationPathId;
        secret.elementPath = this.configurationPath.elementPath;
        secret.attributeName = this.configurationPath.attributeName;
        secret.destinationEndpointId = this.configurationPath.destinationEndpointId;
        secret.dataSourceTypeId = this.configurationPath.dataSourceTypeId;
        secret.displayName = this.configurationPath.displayName;
      });
    }

    this.clientConfigurationSecretService.updateClientConfigSecret(secretsToUpdate)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_value) => {
          this.cancelAddingEditingSecret();
          this.toastService.toastCreate('Secrets updated.', 'Success');

          this.refreshClientSecrets();

          this.dialog.closeAll();
          this.editAddSecretLoading = false;

        },
        error: (_error) => {
          this.dialog.closeAll();
          this.editAddSecretLoading = false;
        },
      });
  }

  editPath() {
    this.editingPath = true;
  }

  confirmUpdatingSecrets(secretsToUpdate: ClientConfigurationSecret[]) {
    if (!this.configurationPath && !this.secretValue) {
      this.toastService.toastCreate('No field is populated to update secret.', 'Danger');
      return;
    }

    if (secretsToUpdate.length > 0) {
      this.secretsToUpdate = secretsToUpdate;
    }

    this.dialog.open(this.confirmDialog);
  }

  confirmUpdate() {
    this.confirmUpdatingSecrets([this.clientConfigurationSecret]);
  }

  confirmMultipleSecretsUpdate() {
    this.confirmUpdatingSecrets([this.clientConfigurationSecret, ...this.matchingSecrets]);
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  // get client from Grid
  getClientFromGrid(event: ClientInformation): void {
    console.log(event)
    this.gridLoading.set(true);
    this.selectedClient = event;

    forkJoin({
      integrations: this.clientIntegrationService.getIntegrationsByClient(this.selectedClient.clientDisplayName),
      identifiers: this.identifierService.getIntegrationIdentifiersByClientInformation(this.selectedClient.clientInformationId),
      secrets: this.clientConfigurationSecretService.getClientConfigSecretByClient(this.selectedClient.losTalkerClientId),
    })
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: ({ integrations, identifiers, secrets }) => {
          this.integrations = integrations;
          this.identifiers = identifiers;
          if (secrets != null)
          {
            this.allSecrets = secrets;
            this.secretsGridSettings.set(this.secretsGridSettings().setData(this.allSecrets));
          }
        },
        complete: () => {
          this.gridLoading.set(false);
        },
      });
  }

  // refresh secrets for selected client - call after adding, editing secret
  refreshClientSecrets(): void {
    this.gridLoading.set(true);
    this.allSecrets = [];

    this.clientConfigurationSecretService.getClientConfigSecretByClient(this.selectedClient.losTalkerClientId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (data) => {
          if (data != null)
          {
            this.allSecrets = data;
            this.secretsGridSettings.set(this.secretsGridSettings().setData(this.allSecrets));
          }
        },
        complete: () => {
          this.gridLoading.set(false);
        },
      });
  }

  // get config path from grid
  getConfigPathFromGrid(event: any): void {
    this.configurationPath = event;
  }

  // get integration from grid
  getIntegrationFromGrid(event: any): void {
    this.selectedIntegration = event;
  }

  openMigrateDialog(): void {
    this.dialog.open(this.migrateDialog);
  }

  migrateSecret(): void {
    this.migratingSecret = true;
    this.dialog.closeAll();
    this.clientConfigurationSecretService.migrateClientConfigSecret(this.clientConfigurationSecret)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (_value) => {
        this.cancelAddingEditingSecret();
        this.toastService.toastCreate('Secret migrated: ' + this.clientConfigurationSecret.displayName, 'Success');

        this.refreshClientSecrets();
        this.editSecret(this.clientConfigurationSecret);

        this.dialog.closeAll();
        this.migratingSecret = false;

      }, 
        error: (_error)  => {
          if(!environment.production){
            this.toastService.toastCreate('Failed to Migrate Secret ' + this.clientConfigurationSecret.displayName + ' ' + _error, 'Warning');
          }
        this.dialog.closeAll();
        this.migratingSecret = false;
      },
  });
  }

  cancelMigrateSecret(): void {
    this.dialog.closeAll();
    this.toastService.toastCreate('Can not get or update secret with current Secret Client.', 'Warning');
  }

  setupSecretGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.allSecrets,
      fixedGrid: true,
      columns: [
        new GridColumnData({ field: 'integrationName', header: 'Integration Name'}),
        new GridColumnData({ field: 'attributeName', header: 'Attribute Name'}),
        new GridColumnData({ field: 'elementPath', header: 'Element Path'}),
        new GridColumnData({ field: 'displayName', header: 'Display Name'}),
        new GridColumnData({ filterable: false, field: 'updatedDate', header: 'Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: true,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
      commandsColumn: {
        enabled: true,
        headerText: 'Edit',
        layout: 'horizontal',
        buttonSpacing: '0.25rem',
        buttons: [
          createEditButton({
            label: 'Edit',
            showIcon: false,
            size: 'small',
          }),
        ],
      },
    });
  }
}
